import type { Localize } from "@/lib/i18n/use-localize";
/**
 * QL-349 — the one filter registry, rendered by both the Settings view editor
 * (`client-view-dialog.tsx`) and the ad-hoc filter bar on `/clients`. Turns
 * the tenant's filterable fields (a fixed set of standard ones, plus every
 * active custom field except `date` and `datetime`) into a flat, localized menu, and
 * converts between a `ClientViewFilter` (the wire shape, `@repo/shared`) and
 * a `DraftFilterValue` (what a filter's input control edits while the row is
 * open) in both directions.
 *
 * Per-field-type filter kinds follow the ticket's table exactly: text/
 * textarea → empty/notEmpty, number → "over" (QL-512), dropdown/radio → is any
 * of, multiselect → contains any of, boolean → yes/no, date and datetime → not offered.
 *
 * QL-512 — every filter is ONE control: numeric filters are one amount with
 * the operator fixed by the field — "Cash over", "<number field> over", and
 * for age both "Age over" and "Age below" — not an operator plus an amount.
 */

import { getCountryName, pickT } from "@repo/shared";
import type {
  ActivityDateField,
  ClientFieldDefinition,
  ClientViewFilter,
  CustomFieldOption,
  DatePeriod,
} from "@repo/shared/types";
import { ACTIVITY_DATE_FIELDS } from "@repo/shared/types";
import { ACTIVITY_DATE_LABELS, T } from "./field-registry.i18n";

export type FilterKind =
  | "isAnyOf"
  | "containsAnyOf"
  | "empty"
  | "amount"
  | "boolean"
  | "datePeriod"
  | "favourite";

/** `archived` options stay labelled (so a filter still using one reads properly) but are offered in a picker only while selected. */
export type FilterFieldOption = { value: string; label: string; archived?: boolean };

/**
 * The options a value picker should list: every live one, plus an archived one
 * only while the filter still uses it. With `unresolvedLabel`, a selected value
 * no option knows (a deleted team) is listed under that label, so it can be
 * unticked.
 */
export function pickerOptions(
  options: FilterFieldOption[] | undefined,
  selected: string[],
  unresolvedLabel?: string,
): FilterFieldOption[] {
  const listed = (options ?? []).filter((o) => !o.archived || selected.includes(o.value));
  if (!unresolvedLabel) return listed;
  const known = new Set((options ?? []).map((o) => o.value));
  const unresolved = selected
    .filter((v) => !known.has(v))
    .map((value) => ({ value, label: unresolvedLabel, archived: true }));
  return [...listed, ...unresolved];
}

/** A number input's draft text → number, or null while blank/invalid (`Number("")` would read as 0). */
function parseDraftNumber(text: string | undefined): number | null {
  if (text === undefined || text.trim() === "") return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : null;
}

/** The working value a filter row's input control edits, before it is valid enough to save. */
export type DraftFilterValue = {
  operator?: string;
  values?: string[];
  value?: string;
  period?: DatePeriod;
};

export type FilterField = {
  /** Stable, unique across the whole registry — the "Add filter" menu's item key. */
  key: string;
  label: string;
  kind: FilterKind;
  group: "standard" | "custom";
  /** `isAnyOf` / `containsAnyOf` only. */
  options?: FilterFieldOption[];
  /** Label for a selected value no option knows (e.g. a deleted team); unset shows the raw value. */
  unresolvedLabel?: string;
  /** Draft → wire filter, or null while the draft is not yet complete enough to save. */
  toFilter: (draft: DraftFilterValue) => ClientViewFilter | null;
  /** Does a saved filter belong to this field? (Used to find which field a saved view's filter is editing.) */
  matches: (filter: ClientViewFilter) => boolean;
  /** Wire filter → the draft its input control should start from. */
  draftFromFilter: (filter: ClientViewFilter) => DraftFilterValue;
};

// ─── Standard "is any of" fields ────────────────────────────────────────────

type IsAnyOfStandardField =
  | "country"
  | "language"
  | "costCategory"
  | "owner"
  | "clientType"
  | "team";

function isAnyOfField(
  key: IsAnyOfStandardField,
  label: string,
  options: FilterFieldOption[],
): FilterField {
  return {
    key,
    label,
    kind: "isAnyOf",
    group: "standard",
    options,
    toFilter: (draft) =>
      draft.values && draft.values.length > 0 ? { type: key, values: draft.values } : null,
    matches: (f) => f.type === key,
    draftFromFilter: (f) => ({ values: "values" in f ? f.values : [] }),
  };
}

function favouriteField(label: string): FilterField {
  return {
    key: "favourite",
    label,
    kind: "favourite",
    group: "standard",
    toFilter: () => ({ type: "favourite", values: ["__ME__"] }),
    matches: (f) => f.type === "favourite",
    draftFromFilter: () => ({}),
  };
}

// ─── Standard empty/not-empty fields ────────────────────────────────────────

type EmptyStandardField = "email" | "mobilePhone" | "orgId" | "externalId";

function standardEmptyField(key: EmptyStandardField, label: string): FilterField {
  return {
    key,
    label,
    kind: "empty",
    group: "standard",
    toFilter: (draft) =>
      draft.operator === "empty" || draft.operator === "notEmpty"
        ? { type: "standardEmpty", field: key, operator: draft.operator }
        : null,
    matches: (f) => f.type === "standardEmpty" && f.field === key,
    draftFromFilter: (f) => (f.type === "standardEmpty" ? { operator: f.operator } : {}),
  };
}

// ─── Amount fields: age / cash (one number, operator fixed by the field) ─────

/**
 * QL-512 — the one operator a cash or number-custom-field filter offers
 * ("<field> over", strictly greater). A saved filter with any other operator
 * (views saved before QL-512 could hold one) is not claimed by its field, so
 * it shows as an unavailable filter and is kept as is rather than silently
 * re-read as "over".
 */
const OVER = "gt";

/**
 * QL-512 — age is two filters, each one number: "Age over" includes the age
 * itself (`gte`: at 50 you are over 50 a moment after the birthday) and "Age
 * below" excludes it (`lt`). Age is compared in completed years, so the two
 * operators a view saved before QL-512 could also hold say the same thing one
 * year along — `gt 50` is `gte 51`, `lte 50` is `lt 51` — and are read that
 * way rather than shown as unavailable.
 */
function ageField(direction: "over" | "below", label: string): FilterField {
  const operator = direction === "over" ? "gte" : "lt";
  const legacyOperator = direction === "over" ? "gt" : "lte";
  return {
    key: `age-${direction}`,
    label,
    kind: "amount",
    group: "standard",
    toFilter: (draft) => {
      const years = parseDraftNumber(draft.value);
      return years === null ? null : { type: "age", operator, years };
    },
    matches: (f) => f.type === "age" && (f.operator === operator || f.operator === legacyOperator),
    draftFromFilter: (f) => {
      if (f.type !== "age") return {};
      return { value: String(f.operator === legacyOperator ? Math.floor(f.years) + 1 : f.years) };
    },
  };
}

function cashField(label: string): FilterField {
  return {
    key: "cash",
    label,
    kind: "amount",
    group: "standard",
    toFilter: (draft) => {
      const amount = parseDraftNumber(draft.value);
      return amount === null ? null : { type: "cash", operator: OVER, amount };
    },
    matches: (f) => f.type === "cash" && f.operator === OVER,
    draftFromFilter: (f) => (f.type === "cash" ? { value: String(f.amount) } : {}),
  };
}

// ─── Date period (stored activity columns + the computed lastClientCommunication) ──

function datePeriodField(
  dateField: ActivityDateField | "lastClientCommunication",
  label: string,
): FilterField {
  const key = `datePeriod-${dateField}`;
  return {
    key,
    label,
    kind: "datePeriod",
    group: "standard",
    toFilter: (draft) =>
      draft.period ? { type: "datePeriod", dateField, period: draft.period } : null,
    matches: (f) => f.type === "datePeriod" && f.dateField === dateField,
    draftFromFilter: (f) => (f.type === "datePeriod" ? { period: f.period } : {}),
  };
}

// ─── Custom fields (dynamic, one per active field except `date` / `datetime`) ─

function customFieldOptions(field: ClientFieldDefinition, localize: Localize): FilterFieldOption[] {
  return (field.options ?? []).map((o: CustomFieldOption) => ({
    value: o.id,
    label: localize(o.labelI18n) || o.id,
    archived: o.isArchived || undefined,
  }));
}

function customFieldField(
  field: ClientFieldDefinition,
  localize: Localize,
  overLabel: (name: string) => string,
): FilterField | null {
  const key = `field-${field.id}`;
  const matchesFieldId = (f: ClientViewFilter) =>
    f.type === "customField" && f.fieldId === field.id;

  switch (field.type) {
    case "text":
    case "textarea":
      return {
        key,
        label: field.name,
        kind: "empty",
        group: "custom",
        toFilter: (draft) =>
          draft.operator === "empty" || draft.operator === "notEmpty"
            ? { type: "customField", fieldId: field.id, operator: draft.operator }
            : null,
        matches: matchesFieldId,
        draftFromFilter: (f) =>
          f.type === "customField" && (f.operator === "empty" || f.operator === "notEmpty")
            ? { operator: f.operator }
            : {},
      };
    case "number":
      return {
        key,
        label: overLabel(field.name),
        kind: "amount",
        group: "custom",
        toFilter: (draft) => {
          const value = parseDraftNumber(draft.value);
          return value === null
            ? null
            : { type: "customField", fieldId: field.id, operator: OVER, value };
        },
        matches: (f) => matchesFieldId(f) && f.type === "customField" && f.operator === OVER,
        draftFromFilter: (f) =>
          f.type === "customField" && f.operator === OVER ? { value: String(f.value) } : {},
      };
    case "boolean":
      return {
        key,
        label: field.name,
        kind: "boolean",
        group: "custom",
        toFilter: (draft) =>
          draft.value === "true" || draft.value === "false"
            ? {
                type: "customField",
                fieldId: field.id,
                operator: "boolean",
                value: draft.value === "true",
              }
            : null,
        matches: matchesFieldId,
        draftFromFilter: (f) =>
          f.type === "customField" && f.operator === "boolean" ? { value: String(f.value) } : {},
      };
    case "dropdown":
    case "radio":
      return {
        key,
        label: field.name,
        kind: "isAnyOf",
        group: "custom",
        options: customFieldOptions(field, localize),
        toFilter: (draft) =>
          draft.values && draft.values.length > 0
            ? { type: "customField", fieldId: field.id, operator: "isAnyOf", values: draft.values }
            : null,
        matches: matchesFieldId,
        draftFromFilter: (f) =>
          f.type === "customField" && "values" in f ? { values: f.values } : {},
      };
    case "multiselect":
      return {
        key,
        label: field.name,
        kind: "containsAnyOf",
        group: "custom",
        options: customFieldOptions(field, localize),
        toFilter: (draft) =>
          draft.values && draft.values.length > 0
            ? {
                type: "customField",
                fieldId: field.id,
                operator: "containsAnyOf",
                values: draft.values,
              }
            : null,
        matches: matchesFieldId,
        draftFromFilter: (f) =>
          f.type === "customField" && "values" in f ? { values: f.values } : {},
      };
    default:
      // "date" / "datetime" (not filterable) and anything the registry doesn't know yet.
      return null;
  }
}

// ─── Registry assembly ───────────────────────────────────────────────────────

export type FilterRegistryInputs = {
  userLanguage: string;
  /** `useLocalize()` — reads option and category names with the tenant-default fallback. */
  localize: Localize;
  /** Every active custom field (any type) — `useEditFields()`, NOT `useDropdownFields()` (too narrow). */
  customFields: ClientFieldDefinition[];
  tenantUsers: Array<{ id: string; name: string }>;
  supportedLanguages: string[];
  costCategories: Array<{ id: string; nameI18n: Record<string, string> }>;
  /** QL-380 — every team of the tenant (`useTeams()`); a label filter, offered whatever the access settings. */
  teams: Array<{ id: string; name: string }>;
  countries: ReadonlyArray<{ code: string }>;
  localeNames: Record<string, string>;
};

/** The full filter menu: standard fields first, then active custom fields (date and datetime excluded). */
export function buildFilterRegistry(inputs: FilterRegistryInputs): FilterField[] {
  const t = pickT(T, inputs.userLanguage);

  const ownerOptions: FilterFieldOption[] = [
    { value: "__ME__", label: t.meCurrentUser },
    ...inputs.tenantUsers.map((u) => ({ value: u.id, label: u.name })),
  ];
  const languageOptions: FilterFieldOption[] = inputs.supportedLanguages.map((code) => ({
    value: code,
    label: inputs.localeNames[code] || code,
  }));
  const costCategoryOptions: FilterFieldOption[] = inputs.costCategories.map((c) => ({
    value: c.id,
    label: inputs.localize(c.nameI18n) || c.id,
  }));
  const countryOptions: FilterFieldOption[] = inputs.countries.map((c) => ({
    value: c.code,
    label: getCountryName(c.code, inputs.userLanguage),
  }));
  const teamOptions: FilterFieldOption[] = inputs.teams.map((team) => ({
    value: team.id,
    label: team.name,
  }));
  const clientTypeOptions: FilterFieldOption[] = [
    { value: "Person", label: t.typePerson },
    { value: "Company", label: t.typeCompany },
    { value: "Group", label: t.typeGroup },
  ];

  const standard: FilterField[] = [
    isAnyOfField("country", t.country, countryOptions),
    isAnyOfField("language", t.language, languageOptions),
    isAnyOfField("costCategory", t.costCategory, costCategoryOptions),
    isAnyOfField("owner", t.owner, ownerOptions),
    isAnyOfField("clientType", t.clientType, clientTypeOptions),
    { ...isAnyOfField("team", t.team, teamOptions), unresolvedLabel: t.deletedTeam },
    favouriteField(t.favourite),
    standardEmptyField("email", t.email),
    standardEmptyField("mobilePhone", t.mobilePhone),
    standardEmptyField("orgId", t.orgId),
    standardEmptyField("externalId", t.externalId),
    ageField("over", t.ageOver),
    ageField("below", t.ageBelow),
    cashField(t.cashOver),
    ...ACTIVITY_DATE_FIELDS.map((field) =>
      datePeriodField(field, pickT(ACTIVITY_DATE_LABELS, inputs.userLanguage)[field]),
    ),
    datePeriodField("lastClientCommunication", t.lastClientCommunication),
  ];

  const custom: FilterField[] = inputs.customFields
    .filter((f) => !f.isStandard)
    .map((f) => customFieldField(f, inputs.localize, t.fieldOver))
    .filter((f): f is FilterField => f !== null);

  return [...standard, ...custom];
}

/** Find which registry field (and its draft) a saved `ClientViewFilter` belongs to. */
export function resolveFilterField(
  filter: ClientViewFilter,
  fields: FilterField[],
): { field: FilterField; draft: DraftFilterValue } | null {
  const field = fields.find((f) => f.matches(filter));
  return field ? { field, draft: field.draftFromFilter(filter) } : null;
}
